import { createContext, useContext, type ReactNode } from 'react'
import { useFeatureFlags } from '../../contexts/FeatureFlagContext'
import { useQuackbackWidget } from '../../hooks/notifications/useQuackbackWidget'
import type { FeedbackKind } from '../../lib/feedback/kinds'

interface FeedbackContextValue {
  isOpen: boolean
  ready: boolean
  /** Notifications/Quackback gated for this user — false → ClickUp-only UX. */
  quackbackEnabled: boolean
  newsUnread: number
  closeFeedback: () => void
  openFeedback: (kind: FeedbackKind) => void
  openChangelog: () => void
  openPortal: () => void
}

const FeedbackContext = createContext<FeedbackContextValue | null>(null)

export function useFeedback() {
  const ctx = useContext(FeedbackContext)
  if (!ctx) {
    throw new Error('useFeedback must be used within FeedbackProvider')
  }
  return ctx
}

/**
 * Owns the Quackback widget singleton (launcher hidden) when the flag is on.
 * Flag off → no Quackback load; feedback buttons open the legacy ClickUp form.
 */
export function FeedbackProvider({ children }: { children: ReactNode }) {
  const { isQuackbackConfigured, getQuackbackBaseUrl, getQuackbackBoardIds } =
    useFeatureFlags()
  const quackbackEnabled = isQuackbackConfigured()
  const publicBaseUrl = quackbackEnabled ? getQuackbackBaseUrl() : null
  const boardIds = getQuackbackBoardIds()

  const {
    ready: widgetReady,
    newsUnread,
    isOpen,
    openChangelog,
    openFeedback,
    close,
    openPortal,
  } = useQuackbackWidget({
    publicBaseUrl,
    useSso: quackbackEnabled,
    boardIds,
  })

  return (
    <FeedbackContext.Provider
      value={{
        isOpen,
        // ClickUp path must not wait on the Quackback SDK.
        ready: quackbackEnabled ? widgetReady : true,
        quackbackEnabled,
        newsUnread,
        closeFeedback: close,
        openFeedback,
        openChangelog,
        openPortal,
      }}
    >
      {children}
    </FeedbackContext.Provider>
  )
}
